import { Grid } from '@mui/material';
import { Formik } from 'formik';
import * as React from 'react';
import * as Sentry from '@sentry/react';
import 'react-phone-input-2/lib/material.css';
import { useInitialPrefix } from 'src/hooks';
import FidoScript from 'src/components/FidoScript';
import { WhiteContainer, FormContainer } from 'src/style/common';
import { EnrichmentTime, getEnrichmentTime, renderPreset, GenericFormProps } from 'src/pages/ManualLookup/GenericForm/utils';
import SingleFormField from 'src/pages/ManualLookup/SingleForm/SingleFormField';
import SubmitGridComponent from 'src/pages/ManualLookup/SubmitGrid';
import { validateFields } from 'src/pages/ManualLookup/utils';

const SingleForm: React.FC<GenericFormProps> = ({
  updateScore,
  updateResolutionId,
  isLoading,
  setResolutionId,
  apiKey,
  jsKey,
  fieldName,
  ip,
  defaultValue,
  generalFormInitialValues,
  setGeneralFormInitialValues,
}) => {
  const initialPrefix = useInitialPrefix();
  const [browserCustomerId, setBrowserCustomerId] = React.useState<string>('');
  const [downloadScript, setDownloadScript] = React.useState<boolean>(false);
  const [enrichmentTime, setEnrichmentTime] = React.useState<EnrichmentTime>('extensive');

  const removeFidoObject = () => {
    window.fido = undefined;
    window.fidoObject = undefined;
    const nodes: any = document.querySelectorAll(`[src='${process.env.REACT_APP_FIDO_JS_SIGNUP_URL}']`);
    try {
      if (nodes[0]) document.head.removeChild(nodes[0]);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  React.useEffect(() => {
    if (defaultValue && !isLoading) {
      const customerID = `TEST-${Math.random().toString(36).substr(2)}`;
      updateResolutionId({
        api_key: apiKey,
        customer_id: customerID,
      });
      updateScore({
        api_key: apiKey,
        [fieldName]: fieldName === 'phone' ? `+${defaultValue}` : defaultValue,
        customer_id: customerID,
      });
    }
    return removeFidoObject();
  }, []);

  return (
    <WhiteContainer style={{ boxShadow: 'none', paddingTop: 24, marginTop: 0 }}>
      {downloadScript && browserCustomerId && <FidoScript customer_id={browserCustomerId} apiKey={jsKey} />}
      <Formik
        initialValues={{
          email: defaultValue || generalFormInitialValues.email,
          customer_id: '',
          first_name: generalFormInitialValues.first_name,
          last_name: generalFormInitialValues.last_name,
          tax_id: '',
          ip: defaultValue || generalFormInitialValues.ip || ip,
          phone: defaultValue ? `${initialPrefix} ${defaultValue}` : generalFormInitialValues.phone === '' ? initialPrefix : generalFormInitialValues.phone,
          browser: true,
          phone_prefix: '',
          domain: {
            value: generalFormInitialValues.domain.value,
            country: 'US',
          },
        }}
        enableReinitialize
        validateOnChange={true}
        validate={(values) => {
          setGeneralFormInitialValues({
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            phone: values.phone,
            ip: values.ip,
            domain: values.domain,
          });

          return validateFields(values, fieldName);
        }}
        onSubmit={(values) => {
          let customerID = `TEST-${Math.random().toString(36).substr(2)}`;
          if (browserCustomerId) customerID = browserCustomerId;
          if (!isLoading) {
            updateResolutionId({
              api_key: apiKey,
              customer_id: customerID,
            });
            updateScore({
              api_key: apiKey,
              [fieldName]: fieldName === 'phone' ? `+${values[fieldName as keyof typeof Formik]}` : values[fieldName as keyof typeof Formik],
              customer_id: customerID,
              max_enrichment_time: getEnrichmentTime(enrichmentTime),
            });
            setBrowserCustomerId('');
            setDownloadScript(false);

            removeFidoObject();
          }
        }}
      >
        {(formikProps) => {
          return (
            <FormContainer
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                formikProps.handleSubmit();
              }}
            >
              <Grid container spacing={3} style={{ marginTop: 0 }}>
                <SingleFormField
                  fieldName={fieldName}
                  formikProps={formikProps}
                  generalFormInitialValues={generalFormInitialValues}
                  initialPrefix={initialPrefix}
                  setGeneralFormInitialValues={setGeneralFormInitialValues}
                />
                {renderPreset(fieldName, setResolutionId)}
                <SubmitGridComponent
                  enrichmentTime={enrichmentTime}
                  setEnrichmentTime={setEnrichmentTime}
                  isDisabled={!!formikProps.errors[fieldName as keyof typeof Formik] || isLoading || (fieldName === 'browser' && !browserCustomerId)}
                  onClickFn={() => formikProps.submitForm()}
                />
              </Grid>
            </FormContainer>
          );
        }}
      </Formik>
    </WhiteContainer>
  );
};

export default SingleForm;
